import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Card,
  Group,
  Stack,
  Box,
  TextInput,
  Select,
  ScrollArea,
  ThemeIcon,
  Badge,
  ActionIcon,
  Loader,
  Paper,
  Divider,
  SimpleGrid,
  Center
} from '@mantine/core';
import { personasAPI } from '../services/api';
import { useSubscription } from '../contexts/SubscriptionContext';
import ProBadge from '../components/ProBadge';
import axios from 'axios';

const AIConciergePage = () => {
  const { isProUser, handleLimitError } = useSubscription();
  const [personas, setPersonas] = useState([]);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const [contextualQuestions, setContextualQuestions] = useState([]);
  const messagesEndRef = useRef(null);
  const scrollAreaRef = useRef(null);



  useEffect(() => {
    // Generate session ID
    setSessionId(`session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

    // Load personas
    loadPersonas();

    // Load any existing conversation or add welcome message
    const savedMessages = localStorage.getItem('aiConciergeMessages');
    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
    } else {
      setMessages([{
        role: 'assistant',
        content: "👋 Hello! I'm your AI Gift Concierge. I'm here to help you find the perfect gifts for your loved ones. Select a persona from the dropdown above to get personalized recommendations based on their profile, or just tell me what you're looking for!",
        timestamp: new Date()
      }]);
    }
  }, []);

  useEffect(() => {
    // Scroll to bottom when messages change
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Save messages to localStorage for persistence (shared with floating chat)
    if (messages.length > 0) {
      localStorage.setItem('aiConciergeMessages', JSON.stringify(messages));
    }
  }, [messages]);

  // Generate contextual questions based on selected persona
  useEffect(() => {
    if (!selectedPersona) {
      setContextualQuestions([
        { label: '🎂 Birthday Gift', context: 'I need help finding a birthday gift' },
        { label: '🎄 Holiday Gift', context: 'I need help finding a holiday gift' },
        { label: '💝 Anniversary', context: 'I need help finding an anniversary gift' },
        { label: '🎓 Graduation', context: 'I need help finding a graduation gift' }
      ]);
      return;
    }

    const persona = personas.find(p => p.id === selectedPersona);
    if (!persona) return;

    const questions = [];
    const currentMonth = new Date().getMonth() + 1; // 1-12

    // Age-based suggestions
    if (persona.age) {
      if (persona.age < 12) {
        questions.push({ label: '🧸 Kids toys & games', context: `Gift ideas for a ${persona.age}-year-old child` });
      } else if (persona.age < 25) {
        questions.push({ label: '🎓 Student gifts', context: `Gift ideas for a ${persona.age}-year-old young adult` });
      } else if (persona.age > 60) {
        questions.push({ label: '👴 Comfort gifts', context: `Gift ideas for a ${persona.age}-year-old senior` });
      }
    }

    // Relationship-based suggestions
    if (persona.relationship) {
      const relationship = persona.relationship.toLowerCase();
      if (relationship.includes('family') || relationship.includes('parent')) {
        questions.push({ label: '👨‍👩‍👧 Family bonding', context: `Gift ideas for family member - ${persona.relationship}` });
      } else if (relationship.includes('friend') || relationship.includes('best friend')) {
        questions.push({ label: '🤝 Friendship gifts', context: `Gift ideas for friend - ${persona.relationship}` });
      } else if (relationship.includes('colleague') || relationship.includes('coworker')) {
        questions.push({ label: '💼 Professional gifts', context: `Gift ideas for colleague - ${persona.relationship}` });
      } else if (relationship.includes('partner') || relationship.includes('spouse')) {
        questions.push({ label: '💕 Romantic gifts', context: `Gift ideas for romantic partner - ${persona.relationship}` });
      }
    }

    // Holiday-based suggestions (current season)
    if (currentMonth >= 11 || currentMonth <= 1) { // Winter holidays
      // Only add general Christmas if favorite holiday is not Christmas
      if (!persona.favorite_holiday || persona.favorite_holiday.toLowerCase() !== 'christmas') {
        questions.push({ label: '🎄 Christmas gifts', context: `Christmas gift ideas for ${persona.name}` });
      }
      if (persona.favorite_holiday) {
        questions.push({ label: `🎁 ${persona.favorite_holiday} gifts`, context: `${persona.favorite_holiday} gift ideas for ${persona.name}` });
      }
    } else if (currentMonth >= 5 && currentMonth <= 8) { // Summer
      questions.push({ label: '🏖️ Summer activities', context: `Summer gift ideas for ${persona.name}` });
    } else if (currentMonth >= 9 && currentMonth <= 10) { // Fall
      questions.push({ label: '🍂 Autumn gifts', context: `Fall/autumn gift ideas for ${persona.name}` });
    }

    // Birthday suggestions
    questions.push({ label: '🎂 Birthday surprises', context: `Birthday gift ideas for ${persona.name}` });

    // Favorite holiday if available
    if (persona.favorite_holiday) {
      questions.push({ label: `🎉 ${persona.favorite_holiday}`, context: `${persona.favorite_holiday} gift ideas for ${persona.name}` });
    }

    // Add some general personalized suggestions
    questions.push({ label: '🎁 Personalized gifts', context: `Personalized gift ideas for ${persona.name}` });
    questions.push({ label: '📚 Hobby gifts', context: `Gift ideas based on ${persona.name}'s interests` });

    // Limit to 6 questions for space
    setContextualQuestions(questions.slice(0, 6));
  }, [selectedPersona, personas]);

  const loadPersonas = async () => {
    try {
      const response = await personasAPI.getAll();
      setPersonas(response.data);
    } catch (error) {
      console.error('Failed to load personas:', error);
    }
  };

  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
      const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight;
      }
    }
  };

  const sendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim() || isLoading) return;

    // Check Pro access
    if (!isProUser) {
      setError('AI Gift Concierge is a Pro-only feature. Upgrade to access this feature!');
      return;
    }

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setIsTyping(true);
    setError(null);

    try {
      const response = await axios.post('/ai-concierge/chat', {
        message: messageText,
        personaId: selectedPersona,
        conversationHistory: messages.slice(-10).map(msg => ({
          role: msg.role,
          content: msg.content
        })),
        sessionId: sessionId
      });

      const aiMessage = {
        role: 'assistant',
        content: response.data.message,
        suggestions: response.data.suggestions,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Handle limit errors
      if (!handleLimitError(error)) {
        setError(error.response?.data?.error || 'Failed to get response. Please try again.');
      }
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleQuickAction = (context) => {
    setInputMessage(context);
    sendMessage(context);
  };

  const clearConversation = () => {
    setMessages([{
      role: 'assistant',
      content: "👋 Conversation cleared! How can I help you find the perfect gift today?",
      timestamp: new Date()
    }]);
    setSessionId(`session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
    localStorage.removeItem('aiConciergeMessages');
  };

  const copySuggestion = (text) => {
    navigator.clipboard.writeText(text);
    // Could add a notification here
  };

  const MessageBubble = ({ message }) => {
    const isAI = message.role === 'assistant';
    
    return (
      <Box
        style={{
          display: 'flex',
          justifyContent: isAI ? 'flex-start' : 'flex-end',
          marginBottom: '1rem'
        }}
      >
        <Paper
          p="md"
          radius="lg"
          style={{
            maxWidth: '80%',
            background: isAI 
              ? 'linear-gradient(135deg, rgba(109, 40, 217, 0.1) 0%, rgba(168, 85, 247, 0.05) 100%)'
              : 'linear-gradient(135deg, rgba(25,113,194,0.2) 0%, rgba(21,170,191,0.1) 100%)',
            border: isAI 
              ? '1px solid rgba(109, 40, 217, 0.2)'
              : '1px solid rgba(25,113,194,0.3)'
          }}
        >
          <Group gap="xs" mb="xs">
            <ThemeIcon
              size="sm"
              radius="xl"
              variant="gradient"
              gradient={isAI 
                ? { from: 'violet', to: 'purple', deg: 45 }
                : { from: 'blue', to: 'cyan', deg: 45 }
              }
            >
              {isAI ? '🤖' : '👤'}
            </ThemeIcon>
            <Text size="xs" c="dimmed" fw={600}>
              {isAI ? 'AI Concierge' : 'You'}
            </Text>
            <Text size="xs" c="dimmed">
              {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Text>
          </Group>
          
          <Text size="sm" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {message.content}
          </Text>

          {message.suggestions && message.suggestions.length > 0 && (
            <Box mt="md">
              <Divider my="sm" label="💡 Suggestions" labelPosition="center" />
              <Stack gap="xs">
                {message.suggestions.map((suggestion, idx) => (
                  <Paper
                    key={idx}
                    p="sm"
                    radius="md"
                    withBorder
                    style={{ background: 'rgba(255, 255, 255, 0.02)' }}
                  >
                    <Group justify="space-between" align="flex-start">
                      <Box style={{ flex: 1 }}>
                        <Text size="sm" fw={600} mb={4}>
                          {suggestion.title || suggestion.name}
                        </Text>
                        {suggestion.description && (
                          <Text size="xs" c="dimmed" lineClamp={2}>
                            {suggestion.description}
                          </Text>
                        )}
                        {suggestion.price && (
                          <Badge size="sm" variant="light" color="green" mt={4}>
                            ${suggestion.price}
                          </Badge>
                        )}
                      </Box>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        onClick={() => copySuggestion(suggestion.title || suggestion.name)}
                      >
                        📋
                      </ActionIcon>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
        </Paper>
      </Box>
    );
  };

  // Check Pro access
  if (!isProUser) {
    return (
      <Box style={{ background: 'linear-gradient(180deg, rgba(109, 40, 217, 0.03) 0%, rgba(0,0,0,0) 100%)' }}>
        <Container size="lg" py="xl" px="md">
          <Card
            p="xl"
            radius="lg"
            withBorder
            style={{
              background: 'linear-gradient(135deg, rgba(109, 40, 217, 0.05) 0%, rgba(0,0,0,0) 100%)',
              borderStyle: 'solid',
              borderWidth: '2px',
              borderColor: 'rgba(109, 40, 217, 0.3)'
            }}
          >
            <Center py="xl">
              <Stack align="center" gap="md" maw={600}>
                <ThemeIcon size={100} radius="xl" variant="gradient" gradient={{ from: 'violet', to: 'purple', deg: 45 }}>
                  <Text size="60px">🤖</Text>
                </ThemeIcon>
                <Group gap="xs">
                  <Title order={1} ta="center">
                    AI Gift Concierge
                  </Title>
                  <ProBadge size="lg" />
                </Group>
                <Text size="xl" c="dimmed" ta="center">
                  Your Personal AI-Powered Gift Expert
                </Text>
                <Text c="dimmed" ta="center" size="lg">
                  Get personalized gift recommendations powered by GPT-4, tailored to your personas' preferences, occasions, and budgets.
                </Text>
                <Stack gap="sm" mt="md" w="100%">
                  <Text fw={600} size="sm">✨ Features:</Text>
                  <SimpleGrid cols={2} spacing="xs">
                    <Text size="sm" c="dimmed">🎯 Personalized Suggestions</Text>
                    <Text size="sm" c="dimmed">💬 Natural Conversation</Text>
                    <Text size="sm" c="dimmed">🎭 Persona Integration</Text>
                    <Text size="sm" c="dimmed">💰 Budget-Aware</Text>
                  </SimpleGrid>
                </Stack>
                <Button
                  size="xl"
                  variant="gradient"
                  gradient={{ from: 'violet', to: 'purple', deg: 45 }}
                  mt="xl"
                  onClick={() => window.location.href = '/account?upgrade=true'}
                  style={{ boxShadow: '0 8px 24px rgba(109, 40, 217, 0.4)' }}
                >
                  ⚡ Upgrade to Pro
                </Button>
              </Stack>
            </Center>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box style={{ 
      background: 'linear-gradient(180deg, rgba(109, 40, 217, 0.03) 0%, rgba(0,0,0,0) 100%)',
      minHeight: 'calc(100vh - 64px)'
    }}>
      <Container size="xl" py="xl" px="md" style={{ height: 'calc(100vh - 64px - 48px)' }}>
        {/* Header */}
        <Box mb="lg">
          <Group justify="space-between" align="flex-start" mb="xs">
            <Box>
              <Group align="center" gap="sm" mb="xs">
                <Title 
                  order={1} 
                  size="h1"
                  fw={700}
                  style={{
                    background: 'linear-gradient(45deg, #6d28d9 0%, #a855f7 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  🤖 AI Gift Concierge
                </Title>
                <ProBadge size="lg" />
              </Group>
              <Text size="lg" c="dimmed" fw={400}>
                Your personal AI-powered gift expert
              </Text>
            </Box>
            <Button
              variant="subtle"
              color="gray"
              onClick={clearConversation}
              leftSection="🗑️"
            >
              Clear Chat
            </Button>
          </Group>
        </Box>

        {/* Main Chat Container */}
        <Card
          radius="lg"
          p="md"
          withBorder
          style={{
            height: 'calc(100% - 120px)',
            display: 'flex',
            flexDirection: 'column',
            background: 'linear-gradient(135deg, rgba(109, 40, 217, 0.02) 0%, rgba(0,0,0,0) 100%)'
          }}
        >
          {/* Controls Bar */}
          <Group mb="md" wrap="wrap">
            <Select
              placeholder="Select a persona (optional)"
              data={personas.map(p => ({ value: p.id.toString(), label: p.name }))}
              value={selectedPersona?.toString()}
              onChange={(value) => setSelectedPersona(value ? parseInt(value) : null)}
              clearable
              style={{ flex: 1, minWidth: 200 }}
              leftSection="🎭"
            />
            <Text size="xs" c="dimmed" style={{ flex: 1 }}>
              {selectedPersona 
                ? `Using ${personas.find(p => p.id === parseInt(selectedPersona))?.name}'s preferences`
                : 'No persona selected - general recommendations'
              }
            </Text>
          </Group>

          {/* Quick Actions */}
          <Box mb="md">
            <Text size="sm" fw={600} mb="xs" c="dimmed">💡 Quick Ideas:</Text>
            <Group gap="xs">
              {contextualQuestions.slice(0, 4).map((question, idx) => (
                <Button
                  key={idx}
                  size="xs"
                  variant="light"
                  color="violet"
                  onClick={() => handleQuickAction(question.context)}
                  disabled={isLoading}
                  style={{ fontSize: '11px', padding: '6px 10px' }}
                >
                  {question.label}
                </Button>
              ))}
            </Group>
          </Box>

          <Divider mb="md" />

          {/* Messages Area */}
          <ScrollArea
            style={{ flex: 1 }}
            type="auto"
            ref={scrollAreaRef}
          >
            <Box p="md">
              {messages.map((message, idx) => (
                <MessageBubble key={idx} message={message} />
              ))}
              
              {isTyping && (
                <Box style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '1rem' }}>
                  <Paper
                    p="md"
                    radius="lg"
                    style={{
                      background: 'linear-gradient(135deg, rgba(109, 40, 217, 0.1) 0%, rgba(168, 85, 247, 0.05) 100%)',
                      border: '1px solid rgba(109, 40, 217, 0.2)'
                    }}
                  >
                    <Group gap="xs">
                      <ThemeIcon size="sm" radius="xl" variant="gradient" gradient={{ from: 'violet', to: 'purple', deg: 45 }}>
                        🤖
                      </ThemeIcon>
                      <Loader size="xs" type="dots" />
                      <Text size="xs" c="dimmed">AI is thinking...</Text>
                    </Group>
                  </Paper>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>
          </ScrollArea>

          {/* Error Display */}
          {error && (
            <Paper
              p="sm"
              radius="md"
              mb="md"
              style={{
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(0,0,0,0) 100%)',
                border: '1px solid rgba(239, 68, 68, 0.3)'
              }}
            >
              <Group gap="xs">
                <Text size="sm" c="red">⚠️ {error}</Text>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="red"
                  onClick={() => setError(null)}
                >
                  ✕
                </ActionIcon>
              </Group>
            </Paper>
          )}

          {/* Input Area */}
          <Group gap="sm" align="flex-end">
            <TextInput
              placeholder="Ask about gift ideas, occasions, budgets..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              disabled={isLoading}
              style={{ flex: 1 }}
              size="md"
              radius="xl"
            />
            <Button
              onClick={() => sendMessage()}
              disabled={isLoading || !inputMessage.trim()}
              size="md"
              radius="xl"
              variant="gradient"
              gradient={{ from: 'violet', to: 'purple', deg: 45 }}
              style={{ minWidth: 100 }}
            >
              {isLoading ? <Loader size="sm" color="white" /> : '📤 Send'}
            </Button>
          </Group>
        </Card>
      </Container>
    </Box>
  );
};

export default AIConciergePage;
